import { Controller } from "@hotwired/stimulus";

const STORAGE_KEY = "inbox:list-width";

/**
 * Lets the user drag the divider between the inbox list and the reading pane to
 * resize them. The width is written to a CSS variable (--inbox-list-width) that
 * only the two-pane `@4xl:` width utility reads, so the single-column layout of
 * a narrow sheet is untouched. The chosen width persists per-browser in localStorage.
 */
export default class InboxResizeController extends Controller {
  static values = {
    min: { type: Number, default: 300 },
    maxFraction: { type: Number, default: 0.6 },
  };

  declare minValue: number;
  declare maxFractionValue: number;

  private dragging = false;
  private currentWidth = 0;
  private readonly onMove = (event: MouseEvent) => this.resizeTo(event);
  private readonly onUp = () => this.endDrag();

  connect(): void {
    const saved = this.savedWidth();
    if (saved !== null) this.applyWidth(saved);
  }

  disconnect(): void {
    this.removeListeners();
  }

  startDrag(event: MouseEvent): void {
    event.preventDefault();
    this.dragging = true;
    document.addEventListener("mousemove", this.onMove);
    document.addEventListener("mouseup", this.onUp);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  }

  private resizeTo(event: MouseEvent): void {
    if (!this.dragging) return;
    const left = (this.element as HTMLElement).getBoundingClientRect().left;
    this.applyWidth(event.clientX - left);
  }

  private endDrag(): void {
    if (!this.dragging) return;
    this.dragging = false;
    this.removeListeners();
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
    try {
      localStorage.setItem(STORAGE_KEY, String(Math.round(this.currentWidth)));
    } catch {
      // localStorage may be unavailable (private mode, sandboxed) — width just won't persist.
    }
  }

  private removeListeners(): void {
    document.removeEventListener("mousemove", this.onMove);
    document.removeEventListener("mouseup", this.onUp);
  }

  private applyWidth(width: number): void {
    const container = this.element as HTMLElement;
    const max = container.clientWidth * this.maxFractionValue;
    this.currentWidth = Math.max(this.minValue, Math.min(width, max || width));
    container.style.setProperty("--inbox-list-width", `${this.currentWidth}px`);
  }

  private savedWidth(): number | null {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
    if (!raw) return null;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
}
